import type { StateCreator } from "zustand";

import type {
  CategoryWithSelectedProduct,
  Product,
} from "@/features/products/models";
import { BILLING_FREQUENCY, PRODUCT_SLUG } from "@/shared/domains";
import type { Category, Price } from "@/shared/domains";
import type { Answer } from "@/shared/models";

const resolveDefaultPrice = (product: Product) => {
  if (product.slug === PRODUCT_SLUG.TIRZEPATIDE_INJECTABLE) {
    return product.prices.find(
      (price) => price.durationInMonths === BILLING_FREQUENCY.QUARTERLY,
    );
  }

  return (
    product.prices.find(
      (price) => price.durationInMonths === BILLING_FREQUENCY.SEMI_ANNUALLY,
    ) ?? product.prices.at(-1)
  );
};

export interface TreatmentSlice {
  selectedCategories: CategoryWithSelectedProduct[];
  eligibilityAnswerIds: Answer["id"][] | null;
  setEligibilityAnswerIds: (
    eligibilityAnswerIds: TreatmentSlice["eligibilityAnswerIds"],
  ) => void;
  setSelectedCategories: (categories: Omit<Category, "treatments">[]) => void;
  addSelectedProduct: (product: Product) => void;
  removeSelectedProduct: (product: Product) => void;
  resetSelectedProducts: () => void;
  setSelectedPrice: (categoryId: Category["id"], selectedPrice: Price) => void;
}

export const createTreatmentSlice: StateCreator<
  TreatmentSlice,
  [],
  [],
  TreatmentSlice
> = (set) => ({
  selectedCategories: [],

  eligibilityAnswerIds: null,

  setEligibilityAnswerIds: (eligibilityAnswerIds) =>
    set({ eligibilityAnswerIds }),

  setSelectedCategories: (selectedCategories) =>
    set({ selectedCategories: selectedCategories.sort((a, b) => a.id - b.id) }),

  addSelectedProduct: (product) =>
    set(({ selectedCategories }) => ({
      selectedCategories: selectedCategories.map((category) =>
        category.id === product.category?.id
          ? {
              ...category,
              selectedProduct: {
                ...product,
                selectedPrice: resolveDefaultPrice(product),
              },
            }
          : category,
      ),
    })),

  removeSelectedProduct: (product) =>
    set(({ selectedCategories }) => ({
      selectedCategories: selectedCategories.map((category) =>
        category.id === product.category?.id
          ? {
              ...category,
              selectedProduct: undefined,
            }
          : category,
      ),
    })),

  resetSelectedProducts: () =>
    set(({ selectedCategories }) => ({
      selectedCategories: selectedCategories.map((category) => ({
        ...category,
        selectedProduct: undefined,
      })),
    })),

  setSelectedPrice: (categoryId, selectedPrice) =>
    set(({ selectedCategories }) => ({
      selectedCategories: selectedCategories.map((category) =>
        category.id === categoryId
          ? {
              ...category,
              selectedProduct: category.selectedProduct && {
                ...category.selectedProduct,
                selectedPrice,
              },
            }
          : category,
      ),
    })),
});
